/**
 * GeoExtractor - Geofence Data Extraction Page
 * 
 * Modern MUI implementation of the WenkTag geofence extractor.
 * Loads Wialon resources, lets the user pick one, then extracts
 * and displays geofence data in a searchable, exportable table.
 * 
 * Light theme matching the overall Portal design.
 */

import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { keyframes } from '@emotion/react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import LinearProgress from '@mui/material/LinearProgress';
import Dialog from '@mui/material/Dialog';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FilterListIcon from '@mui/icons-material/FilterList';
import OutlinedInput from '@mui/material/OutlinedInput';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import Collapse from '@mui/material/Collapse';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import PlaceIcon from '@mui/icons-material/Place';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ImageIcon from '@mui/icons-material/Image';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import TableChartIcon from '@mui/icons-material/TableChart';
import MapIcon from '@mui/icons-material/Map';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import { selectWialonSession } from '../../reduxStore/WialonSessionReducer';
import WialonService from '../../Network/Wialon/WialonService';
import {
    processZones,
    standardHeaders,
    standardHeadersEn,
    standardDataKeys,
    isWenkTagGeofence,
} from './geoExtractorUtils';
import XLSX from 'xlsx-js-style';
import * as FileSaver from 'file-saver';
import '../../styles/ModernStyles.css';

// Leaflet
import { MapContainer, TileLayer, Marker, Popup, LayersControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
const { BaseLayer } = LayersControl;

// ── Animations ───────────────────────────────────────────────────────
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
`;

// ── Sorting helper ───────────────────────────────────────────────────
function descendingComparator(a, b, orderBy) {
    if ((b[orderBy] || '') < (a[orderBy] || '')) return -1;
    if ((b[orderBy] || '') > (a[orderBy] || '')) return 1;
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

// ── Custom Leaflet Marker Icon ──────────────────────────────────────
const customIcon = new L.Icon({
    iconUrl: 'data:image/svg+xml;base64,' + btoa(`
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42">
            <path fill="#4ECDC4" stroke="#fff" stroke-width="2" d="M16 0C7.163 0 0 7.163 0 16c0 12 16 26 16 26s16-14 16-26C32 7.163 24.837 0 16 0z"/>
            <circle cx="16" cy="16" r="6" fill="#fff"/>
        </svg>
    `),
    iconSize: [32, 42],
    iconAnchor: [16, 42],
    popupAnchor: [0, -42],
});

// ── MAIN COMPONENT ──────────────────────────────────────────────────
export default function GeoExtractor() {
    const [t] = useTranslation('common');
    const session = useSelector(selectWialonSession);
    const directions = useSelector((state) => state.settingsData.directions);
    const gridtheme = useSelector((state) => state.themeData.gridtheme);
    const isRTL = directions === 'rtl';

    // Data state
    const [resources, setResources] = useState([]);
    const [selectedResource, setSelectedResource] = useState('');
    const [rows, setRows] = useState([]);
    const [dynamicLabels, setDynamicLabels] = useState([]);

    // UI state
    const [loadingResources, setLoadingResources] = useState(true);
    const [loadingZones, setLoadingZones] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('table'); // 'table' | 'map'
    const [isMapFullscreen, setIsMapFullscreen] = useState(false);

    // Table state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('name');

    // Image preview state
    const [imagePreview, setImagePreview] = useState({ open: false, url: '', name: '' });
    const [hideEmptyResources, setHideEmptyResources] = useState(true);

    // Advanced search states
    const [searchSalesman, setSearchSalesman] = useState('');
    const [searchDynamicFields, setSearchDynamicFields] = useState('');
    const [selectedSearchColumns, setSelectedSearchColumns] = useState([]);
    const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);

    // Details Dialog state
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const [selectedRowForDetails, setSelectedRowForDetails] = useState(null);

    // Actions menu state
    const [actionsMenuAnchorEl, setActionsMenuAnchorEl] = useState(null);
    const [actionsMenuRow, setActionsMenuRow] = useState(null);

    const hasFetched = useRef(false);

    // ── WenkTag Detection Helpers ─────────────────────────────────────
    const getWenkTagCount = useCallback((res) => {
        const zones = res?.zl ? (Array.isArray(res.zl) ? res.zl : Object.values(res.zl)) : [];
        return zones.filter(zone => isWenkTagGeofence(zone.d)).length;
    }, []);

    const isWenkTagResource = useCallback((res) => {
        return getWenkTagCount(res) > 0;
    }, [getWenkTagCount]);

    // ── Filtered list of resources ────────────────────────────────────
    const filteredResources = useMemo(() => {
        if (!hideEmptyResources) return resources;
        return resources.filter(isWenkTagResource);
    }, [resources, hideEmptyResources, isWenkTagResource]);

    // ── Load resources on mount ──────────────────────────────────────
    useEffect(() => {
        const fetchResources = async () => {
            if (!session?.sid || !session?.baseUrl) {
                setLoadingResources(false);
                return;
            }
            if (hasFetched.current) return;
            hasFetched.current = true;

            setLoadingResources(true);
            setError(null);
            try {
                const result = await WialonService.loadResources(session.baseUrl, session.sid);
                const items = result?.items || [];
                setResources(items);
                // Auto-select first resource from filtered list
                const filtered = items.filter(res => !hideEmptyResources || isWenkTagResource(res));
                if (filtered.length > 0) {
                    const firstId = filtered[0].id.toString();
                    setSelectedResource(firstId);
                    fetchZones(filtered[0]);
                }
            } catch (err) {
                console.error('[GeoExtractor] Failed to load resources:', err);
                setError(t('GEO_EXTRACTOR.loadError') || 'Failed to load resources');
            } finally {
                setLoadingResources(false);
            }
        };
        fetchResources();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session?.sid, session?.baseUrl]);

    // ── Fetch zones for a resource ───────────────────────────────────
    const fetchZones = useCallback(async (resource) => {
        if (!resource) return;
        setLoadingZones(true);
        setError(null);
        setRows([]);
        setDynamicLabels([]);
        setPage(0);
        setSearchTerm('');

        try {
            // The zones are already loaded with the resource via the zones flag
            const zones = resource.zl || {};
            const { rows: parsed, dynamicLabels: dynLabels } = processZones(zones, session?.baseUrl, session?.sid);
            setRows(parsed);
            setDynamicLabels(Array.from(dynLabels));
        } catch (err) {
            console.error('[GeoExtractor] Failed to process zones:', err);
            setError(t('GEO_EXTRACTOR.processError') || 'Failed to process geofence data');
        } finally {
            setLoadingZones(false);
        }
    }, [t, session?.baseUrl, session?.sid]);

    // ── Resource change handler ──────────────────────────────────────
    const handleResourceChange = useCallback((event) => {
        const id = event.target.value;
        setSelectedResource(id);
        const res = resources.find((r) => r.id.toString() === id);
        if (res) fetchZones(res);
    }, [resources, fetchZones]);

    // ── Refresh handler ──────────────────────────────────────────────
    const handleRefresh = useCallback(async () => {
        if (!session?.sid || !session?.baseUrl) return;
        setLoadingResources(true);
        setError(null);
        try {
            const result = await WialonService.loadResources(session.baseUrl, session.sid);
            const items = result?.items || [];
            setResources(items);
            if (selectedResource) {
                const res = items.find((r) => r.id.toString() === selectedResource);
                if (res) fetchZones(res);
            } else {
                // If nothing selected, pick first from filtered
                const filtered = items.filter(res => !hideEmptyResources || isWenkTagResource(res));
                if (filtered.length > 0) {
                    const firstId = filtered[0].id.toString();
                    setSelectedResource(firstId);
                    fetchZones(filtered[0]);
                }
            }
        } catch (err) {
            setError(t('GEO_EXTRACTOR.loadError') || 'Failed to reload');
        } finally {
            setLoadingResources(false);
        }
    }, [session, selectedResource, fetchZones, t, hideEmptyResources, isWenkTagResource]);

    // ── Column definitions ───────────────────────────────────────────
    const columns = useMemo(() => {
        const headers = isRTL ? standardHeaders : standardHeadersEn;
        const cols = headers.map((h, i) => ({
            id: standardDataKeys[i],
            label: h,
        }));
        dynamicLabels.forEach((label) => {
            cols.push({ id: label, label });
        });
        return cols;
    }, [dynamicLabels, isRTL]);

    // ── Filtered + sorted data ───────────────────────────────────────
    const filteredRows = useMemo(() => {
        let result = rows;

        // 1. General Search (All columns)
        if (searchTerm.trim()) {
            const term = searchTerm.trim().toLowerCase();
            result = result.filter((row) =>
                Object.values(row).some((val) =>
                    String(val).toLowerCase().includes(term)
                )
            );
        }

        // 2. Salesman Search
        if (searchSalesman.trim()) {
            const term = searchSalesman.trim().toLowerCase();
            result = result.filter((row) => {
                const salesmanVal = String(
                    row['mandoob'] ||
                    row['driver'] ||
                    row['اسم صاحب العمل'] ||
                    row['Salesman'] ||
                    ''
                );
                return salesmanVal.toLowerCase().includes(term);
            });
        }

        // 3. Dynamic Fields Search
        if (searchDynamicFields.trim() && selectedSearchColumns.length > 0) {
            const term = searchDynamicFields.trim().toLowerCase();
            result = result.filter((row) => {
                return selectedSearchColumns.some((colId) => {
                    const colVal = String(row[colId] || '');
                    return colVal.toLowerCase().includes(term);
                });
            });
        }

        return result;
    }, [rows, searchTerm, searchSalesman, searchDynamicFields, selectedSearchColumns]);

    const sortedRows = useMemo(() => {
        return [...filteredRows].sort(getComparator(order, orderBy));
    }, [filteredRows, order, orderBy]);

    const paginatedRows = useMemo(() => {
        if (rowsPerPage === -1) return sortedRows;
        return sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [sortedRows, page, rowsPerPage]);

    // ── Sort handler ─────────────────────────────────────────────────
    const handleSort = (columnId) => {
        const isAsc = orderBy === columnId && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(columnId);
    };

    // ── Export to Excel ──────────────────────────────────────────────
    const handleExport = useCallback(() => {
        if (filteredRows.length === 0) return;

        const resName = resources.find(r => r.id.toString() === selectedResource)?.nm || 'geofences';
        const exportDate = new Date().toLocaleString();
        const colCount = columns.length;

        // 1. Prepare Rows for AoA (Array of Arrays)
        const headerData = [
            [`WenkTag Geofence Report — ${resName}`],
            [`Generated on: ${exportDate} | Total items: ${filteredRows.length}`],
            [], // Spacer
        ];
        const tableHeaders = columns.map(col => col.label);
        const dataRows = filteredRows.map(row => columns.map(col => row[col.id] || '----'));
        const worksheetData = [...headerData, tableHeaders, ...dataRows];

        const ws = XLSX.utils.aoa_to_sheet(worksheetData);

        // 2. Define Styles
        const commonBorder = {
            top: { style: "thin", color: { rgb: "E2E8F0" } },
            bottom: { style: "thin", color: { rgb: "E2E8F0" } },
            left: { style: "thin", color: { rgb: "E2E8F0" } },
            right: { style: "thin", color: { rgb: "E2E8F0" } }
        };

        const titleStyle = {
            font: { bold: true, sz: 16, color: { rgb: "1E293B" } },
            alignment: { horizontal: "center", vertical: "center" }
        };

        const metadataStyle = {
            font: { sz: 10, color: { rgb: "64748B" } },
            alignment: { horizontal: "center", vertical: "center" }
        };

        const headerStyle = {
            fill: { fgColor: { rgb: "4ECDC4" } },
            font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12 },
            alignment: { horizontal: isRTL ? "right" : "left", vertical: "center" },
            border: commonBorder
        };

        const cellStyle = {
            font: { sz: 11, color: { rgb: "1E293B" } },
            alignment: { vertical: "center", horizontal: isRTL ? "right" : "left" },
            border: commonBorder
        };

        const alternateCellStyle = {
            ...cellStyle,
            fill: { fgColor: { rgb: "F8FAFC" } }
        };

        // 3. Apply Styles to Cells
        const range = XLSX.utils.decode_range(ws['!ref']);
        for (let R = range.s.r; R <= range.e.r; ++R) {
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const cell_address = { c: C, r: R };
                const cell_ref = XLSX.utils.encode_cell(cell_address);
                if (!ws[cell_ref]) continue;

                // Handle Header Rows (0: Title, 1: Metadata)
                if (R === 0) ws[cell_ref].s = titleStyle;
                else if (R === 1) ws[cell_ref].s = metadataStyle;
                // Table Header Row (Index 3)
                else if (R === 3) ws[cell_ref].s = headerStyle;
                // Data Rows (Index 4+)
                else if (R > 3) {
                    ws[cell_ref].s = (R % 2 === 0) ? alternateCellStyle : cellStyle;
                }
            }
        }

        // 4. Layout: Merges & RTL
        ws['!merges'] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: Math.max(colCount - 1, 0) } },
            { s: { r: 1, c: 0 }, e: { r: 1, c: Math.max(colCount - 1, 0) } }
        ];

        if (isRTL) ws['!views'] = [{ RTL: true }];

        const colWidths = columns.map((col) => {
            let maxLen = col.label.length;
            filteredRows.forEach((row) => {
                const val = String(row[col.id] || '----');
                if (val.length > maxLen) maxLen = val.length;
            });
            return { wch: Math.max(maxLen + 4, 15) };
        });
        ws['!cols'] = colWidths;

        // 5. Create Workbook and Write
        const wb = XLSX.utils.book_new();
        if (isRTL) wb.Workbook = { Views: [{ RTL: true }] };
        XLSX.utils.book_append_sheet(wb, ws, 'Geofences');

        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([buf], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
        });

        FileSaver.saveAs(blob, `${resName}_geo_${Math.floor(Date.now() / 1000)}.xlsx`);
    }, [filteredRows, columns, resources, selectedResource, isRTL]);

    // ── Open location in Google Maps ────────────────────────────────
    const handleOpenLocation = useCallback((lat, lng) => {
        if (lat && lng) {
            window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
        }
    }, []);

    // ── Open image preview ──────────────────────────────────────────
    const handleOpenImage = useCallback((url, name) => {
        setImagePreview({ open: true, url, name });
    }, []);

    const handleCloseImage = useCallback(() => {
        setImagePreview({ open: false, url: '', name: '' });
    }, []);

    // ── Print handler ────────────────────────────────────────────────
    const handlePrint = useCallback(() => {
        const resName = resources.find(r => r.id.toString() === selectedResource)?.nm || 'Geofences';
        const printWin = window.open('', '_blank');
        if (!printWin) return;

        // Dynamically compute font size: shrink when many columns
        const colCount = columns.length;
        const fontSize = colCount > 12 ? 7 : colCount > 9 ? 8 : 10;
        const headerFontSize = fontSize + 1;
        const cellPad = colCount > 12 ? '3px 4px' : '4px 6px';

        const headerRow = columns.map(c => `<th>${c.label}</th>`).join('');
        const bodyRows = filteredRows.map(row => {
            const cells = columns.map(c => `<td>${row[c.id] || '----'}</td>`).join('');
            return `<tr>${cells}</tr>`;
        }).join('');

        printWin.document.write(`
      <!DOCTYPE html>
      <html dir="${isRTL ? 'rtl' : 'ltr'}">
      <head>
        <title>${resName} — WenkTag Geofences</title>
        <style>
          @page {
            size: landscape;
            margin: 8mm;
          }
          * { box-sizing: border-box; }
          body {
            font-family: 'Cairo', 'Segoe UI', Arial, sans-serif;
            padding: 0;
            margin: 0;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          h2 { color: #1E293B; margin: 0 0 2px 0; font-size: 14px; }
          .subtitle { color: #64748B; margin: 0 0 8px 0; font-size: 10px; }
          table {
            width: 100%;
            border-collapse: collapse;
            table-layout: auto;
          }
          th {
            padding: ${cellPad};
            border: 1px solid #CBD5E1;
            background: #F1F5F9;
            font-size: ${headerFontSize}px;
            font-weight: 700;
            white-space: nowrap;
            color: #334155;
          }
          td {
            padding: ${cellPad};
            border: 1px solid #E2E8F0;
            font-size: ${fontSize}px;
            color: #1E293B;
            white-space: nowrap;
          }
          tr:nth-child(even) { background: #F8FAFC; }
        </style>
      </head>
      <body>
        <h2>${resName}</h2>
        <p class="subtitle">${filteredRows.length} geofences — ${new Date().toLocaleDateString()}</p>
        <table>
          <thead><tr>${headerRow}</tr></thead>
          <tbody>${bodyRows}</tbody>
        </table>
      </body>
      </html>
    `);
        printWin.document.close();
        printWin.focus();

        // Close the tab after printing or canceling
        printWin.onafterprint = () => printWin.close();
        setTimeout(() => {
            printWin.print();
            // Fallback for browsers that don't fire onafterprint
            setTimeout(() => { try { printWin.close(); } catch (e) { /* already closed */ } }, 1000);
        }, 500);
    }, [filteredRows, columns, resources, selectedResource, isRTL]);

    // ═════════════════════════════════════════════════════════════════
    // RENDER
    // ═════════════════════════════════════════════════════════════════

    return (
        <Box
            sx={{
                minHeight: 'calc(100vh - 120px)',
                background: 'linear-gradient(135deg, #F8FAFC 0%, #EEF2F7 100%)',
                borderRadius: 3,
                padding: 3,
                animation: `${fadeInUp} 0.5s ease-out`,
            }}
        >
            {/* ── Page Header ─────────────────────────────────────────── */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                <Box
                    sx={{
                        width: 42, height: 42, borderRadius: 2,
                        background: 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                >
                    <PlaceIcon sx={{ color: '#fff', fontSize: 22 }} />
                </Box>
                <Box>
                    <Typography
                        sx={{
                            fontSize: 22, fontWeight: 700, color: '#1E293B',
                            fontFamily: 'Cairo, sans-serif',
                        }}
                    >
                        {t('GEO_EXTRACTOR.title') || 'Geo Extractor'}
                    </Typography>
                    <Typography
                        sx={{ fontSize: 13, color: '#64748B', fontFamily: 'Cairo, sans-serif', mt: -0.3 }}
                    >
                        {t('GEO_EXTRACTOR.subtitle') || 'Extract and browse geofence data from your resources'}
                    </Typography>
                </Box>
            </Box>

            {/* ── Toolbar Card ────────────────────────────────────────── */}
            <Paper
                elevation={0}
                sx={{
                    mt: 2.5, p: 2, borderRadius: 3,
                    border: '1px solid #E2E8F0',
                    display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center',
                    animation: `${fadeInUp} 0.5s ease-out 0.1s both`,
                }}
            >
                {/* Resource selector */}
                <FormControl sx={{ minWidth: 260, flex: '1 1 260px' }} size="small">
                    <InputLabel
                        sx={{ fontFamily: 'Cairo, sans-serif', fontSize: 14 }}
                    >
                        {t('GEO_EXTRACTOR.selectResource') || 'Select Resource'}
                    </InputLabel>
                    <Select
                        value={selectedResource}
                        onChange={handleResourceChange}
                        label={t('GEO_EXTRACTOR.selectResource') || 'Select Resource'}
                        disabled={loadingResources}
                        sx={{
                            fontFamily: 'Cairo, sans-serif', fontSize: 14,
                            borderRadius: 2,
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4ECDC4' },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4ECDC4' },
                        }}
                    >
                        {filteredResources.map((res) => {
                            const count = getWenkTagCount(res);
                            return (
                                <MenuItem key={res.id} value={res.id.toString()}
                                    sx={{ fontFamily: 'Cairo, sans-serif', fontSize: 14 }}
                                >
                                    {res.nm} {hideEmptyResources ? '' : `(${count})`}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>

                <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', gap: 1 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={hideEmptyResources}
                                onChange={(e) => setHideEmptyResources(e.target.checked)}
                                sx={{
                                    color: '#E2E8F0',
                                    '&.Mui-checked': { color: '#4ECDC4' },
                                    py: 0.5
                                }}
                            />
                        }
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <FilterListIcon sx={{ fontSize: 16, color: '#94A3B8' }} />
                                <Typography sx={{ fontFamily: 'Cairo, sans-serif', fontSize: 13, color: '#64748B' }}>
                                    {t('GEO_EXTRACTOR.hideEmpty') || 'Hide Empty'}
                                </Typography>
                            </Box>
                        }
                        sx={{ mr: 2 }}
                    />
                </Box>
                {/* Search */}
                <TextField
                    size="small"
                    placeholder={t('GEO_EXTRACTOR.search') || 'Search...'}
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                    sx={{
                        minWidth: 200, flex: '1 1 200px',
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2, fontFamily: 'Cairo, sans-serif', fontSize: 14,
                            '& fieldset': { borderColor: '#E2E8F0' },
                            '&:hover fieldset': { borderColor: '#4ECDC4' },
                            '&.Mui-focused fieldset': { borderColor: '#4ECDC4' },
                        },
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#94A3B8', fontSize: 20 }} />
                            </InputAdornment>
                        ),
                    }}
                />

                {/* View Mode Toggle */}
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', gap: 1 }}>
                    <ToggleButtonGroup
                        size="small"
                        value={viewMode}
                        exclusive
                        onChange={(e, val) => val && setViewMode(val)}
                        sx={{
                            backgroundColor: '#fff',
                            borderRadius: '10px',
                            border: '1px solid #E2E8F0',
                            padding: '3px',
                            '& .MuiToggleButton-root': {
                                px: 2,
                                py: 0.5,
                                border: '1px solid transparent !important',
                                borderRadius: '8px !important',
                                fontFamily: 'Cairo, sans-serif',
                                fontSize: 13,
                                fontWeight: 700,
                                color: '#64748B',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                '&.Mui-selected': {
                                    backgroundColor: 'rgba(78, 205, 196, 0.05)',
                                    color: '#4ECDC4',
                                    border: '1px solid #4ECDC4 !important',
                                    '&:hover': { backgroundColor: 'rgba(78, 205, 196, 0.1)' }
                                }
                            }
                        }}
                    >
                        <ToggleButton value="table">
                            <Typography sx={{ fontFamily: 'Cairo, sans-serif', fontSize: 13, fontWeight: 700, color: 'inherit' }}>
                                {t('GEO_EXTRACTOR.viewTable') || 'Table View'}
                            </Typography>
                            <TableChartIcon sx={{ fontSize: 18 }} />
                        </ToggleButton>
                        <ToggleButton value="map">
                            <Typography sx={{ fontFamily: 'Cairo, sans-serif', fontSize: 13, fontWeight: 700, color: 'inherit' }}>
                                {t('GEO_EXTRACTOR.viewMap') || 'Map View'}
                            </Typography>
                            <MapIcon sx={{ fontSize: 18 }} />
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                {/* ── Action buttons Row ────────────────────────────────── */}
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', animation: `${fadeInUp} 0.5s ease-out 0.2s both` }}>
                    <Tooltip title={t('GEO_EXTRACTOR.refresh') || 'Refresh'}>
                        <IconButton onClick={handleRefresh} disabled={loadingResources || loadingZones}
                            sx={{
                                background: 'rgba(78, 205, 196, 0.08)', borderRadius: 2,
                                '&:hover': { background: 'rgba(78, 205, 196, 0.15)' },
                            }}
                        >
                            <RefreshIcon sx={{ color: '#4ECDC4', fontSize: 20 }} />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={t('GEO_EXTRACTOR.export') || 'Export Excel'}>
                        <span>
                            <IconButton onClick={handleExport} disabled={filteredRows.length === 0}
                                sx={{
                                    background: 'rgba(16, 185, 129, 0.08)', borderRadius: 2,
                                    '&:hover': { background: 'rgba(16, 185, 129, 0.15)' },
                                }}
                            >
                                <FileDownloadIcon sx={{ color: '#10B981', fontSize: 20 }} />
                            </IconButton>
                        </span>
                    </Tooltip>

                    <Tooltip title={t('GEO_EXTRACTOR.print') || 'Print'}>
                        <span>
                            <IconButton onClick={handlePrint} disabled={filteredRows.length === 0}
                                sx={{
                                    background: 'rgba(108, 155, 207, 0.08)', borderRadius: 2,
                                    '&:hover': { background: 'rgba(108, 155, 207, 0.15)' },
                                }}
                            >
                                <PrintIcon sx={{ color: '#6C9BCF', fontSize: 20 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>

                <Tooltip title={t('GEO_EXTRACTOR.advancedSearch') || 'Advanced Search'}>
                    <IconButton
                        onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
                        sx={{
                            background: showAdvancedSearch ? 'rgba(78, 205, 196, 0.15)' : 'rgba(78, 205, 196, 0.08)',
                            borderRadius: 2,
                            color: '#4ECDC4',
                            '&:hover': { background: 'rgba(78, 205, 196, 0.15)' },
                        }}
                    >
                        <FilterListIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Tooltip>
            </Paper>

            {/* ── Advanced Search Group ────────────────────────────────── */}
            <Collapse in={showAdvancedSearch}>
                {rows.length > 0 && (
                    <Paper
                        elevation={0}
                        sx={{
                            mt: 2, p: 2, borderRadius: 3,
                            border: '1px solid #E2E8F0',
                            backgroundColor: '#F8FAFC',
                            animation: `${fadeInUp} 0.5s ease-out 0.15s both`,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                            <FilterListIcon sx={{ color: '#4ECDC4', fontSize: 20 }} />
                            <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#1E293B', fontFamily: 'Cairo, sans-serif' }}>
                                {t('GEO_EXTRACTOR.advancedSearch') || 'Advanced Filters'}
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={() => {
                                    setSearchSalesman('');
                                    setSearchDynamicFields('');
                                    setSelectedSearchColumns([]);
                                    setSearchTerm('');
                                }}
                                sx={{ ml: 'auto' }}
                            >
                                <Tooltip title={t('GEO_EXTRACTOR.resetFilters') || 'Reset Filters'}>
                                    <RefreshIcon sx={{ fontSize: 18, color: '#94A3B8' }} />
                                </Tooltip>
                            </IconButton>
                        </Box>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5 }}>
                            {/* Salesman Search */}
                            <Box sx={{ flex: '1 1 300px' }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#64748B', fontFamily: 'Cairo, sans-serif', mb: 0.8, ml: 0.5 }}>
                                    {t('ADVANCESEARCH.salesEmployee') || 'Salesman'}
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder={t('GEO_EXTRACTOR.searchSalesman') || 'Search Salesman...'}
                                    value={searchSalesman}
                                    onChange={(e) => { setSearchSalesman(e.target.value); setPage(0); }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2, fontFamily: 'Cairo, sans-serif', fontSize: 14,
                                            backgroundColor: '#fff',
                                            '& fieldset': { borderColor: '#E2E8F0' },
                                            '&:hover fieldset': { borderColor: '#4ECDC4' },
                                            '&.Mui-focused fieldset': { borderColor: '#4ECDC4' },
                                        },
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: '#94A3B8', fontSize: 18 }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>

                            {/* Column Selector */}
                            <Box sx={{ flex: '1 1 250px' }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#64748B', fontFamily: 'Cairo, sans-serif', mb: 0.8, ml: 0.5 }}>
                                    {t('GEO_EXTRACTOR.selectColumns') || 'Select Search Columns'}
                                </Typography>
                                <FormControl fullWidth size="small">
                                    <Select
                                        multiple
                                        value={selectedSearchColumns}
                                        onChange={(e) => { setSelectedSearchColumns(e.target.value); setPage(0); }}
                                        input={<OutlinedInput />}
                                        renderValue={(selected) => selected.map(id => columns.find(c => c.id === id)?.label || id).join(', ')}
                                        sx={{
                                            fontFamily: 'Cairo, sans-serif', fontSize: 14,
                                            borderRadius: 2, backgroundColor: '#fff',
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4ECDC4' },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4ECDC4' },
                                        }}
                                    >
                                        {columns.filter(c => c.id !== 'name').map((col) => (
                                            <MenuItem key={col.id} value={col.id} sx={{ fontFamily: 'Cairo, sans-serif' }}>
                                                <Checkbox size="small" checked={selectedSearchColumns.indexOf(col.id) > -1} />
                                                <ListItemText primary={col.label} primaryTypographyProps={{ fontFamily: 'Cairo, sans-serif', fontSize: 14 }} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Dynamic Fields Search */}
                            <Box sx={{ flex: '1 1 300px' }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#64748B', fontFamily: 'Cairo, sans-serif', mb: 0.8, ml: 0.5 }}>
                                    {t('GEO_EXTRACTOR.searchDynamicFields') || 'Search in Columns'}
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder={t('GEO_EXTRACTOR.searchDynamicFields') || 'Type keywords...'}
                                    value={searchDynamicFields}
                                    onChange={(e) => { setSearchDynamicFields(e.target.value); setPage(0); }}
                                    disabled={selectedSearchColumns.length === 0}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2, fontFamily: 'Cairo, sans-serif', fontSize: 14,
                                            backgroundColor: '#fff',
                                            '& fieldset': { borderColor: '#E2E8F0' },
                                            '&:hover fieldset': { borderColor: '#4ECDC4' },
                                            '&.Mui-focused fieldset': { borderColor: '#4ECDC4' },
                                        },
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: selectedSearchColumns.length === 0 ? '#CBD5E1' : '#94A3B8', fontSize: 18 }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                        </Box>
                    </Paper>
                )}
            </Collapse>

            {/* ── Stats Bar ───────────────────────────────────────────── */}
            {
                !loadingZones && rows.length > 0 && (
                    <Box
                        sx={{
                            mt: 1.5, display: 'flex', gap: 2, flexWrap: 'wrap',
                            animation: `${fadeInUp} 0.5s ease-out 0.2s both`,
                        }}
                    >
                        <Chip
                            label={`${rows.length} ${t('GEO_EXTRACTOR.totalPoints') || 'Total Points'}`}
                            sx={{
                                fontFamily: 'Cairo, sans-serif', fontWeight: 600, fontSize: 12,
                                background: 'rgba(78, 205, 196, 0.1)', color: '#4ECDC4',
                                border: '1px solid rgba(78, 205, 196, 0.2)',
                            }}
                        />
                        {searchTerm && (
                            <Chip
                                label={`${filteredRows.length} ${t('GEO_EXTRACTOR.matchingResults') || 'Matching'}`}
                                sx={{
                                    fontFamily: 'Cairo, sans-serif', fontWeight: 600, fontSize: 12,
                                    background: 'rgba(108, 155, 207, 0.1)', color: '#6C9BCF',
                                    border: '1px solid rgba(108, 155, 207, 0.2)',
                                }}
                            />
                        )}
                        {dynamicLabels.length > 0 && (
                            <Chip
                                label={`+${dynamicLabels.length} ${t('GEO_EXTRACTOR.extraFields') || 'Extra Fields'}`}
                                sx={{
                                    fontFamily: 'Cairo, sans-serif', fontWeight: 600, fontSize: 12,
                                    background: 'rgba(245, 158, 11, 0.1)', color: '#F59E0B',
                                    border: '1px solid rgba(245, 158, 11, 0.2)',
                                }}
                            />
                        )}
                    </Box>
                )
            }

            {/* ── Loading / Error / Empty States ──────────────────────── */}
            {
                (loadingResources || loadingZones) && (
                    <Box
                        sx={{
                            mt: 4, display: 'flex', flexDirection: 'column',
                            alignItems: 'center', gap: 2, py: 8,
                        }}
                    >
                        <CircularProgress sx={{ color: '#4ECDC4' }} size={44} />
                        <Typography
                            sx={{
                                fontSize: 15, color: '#64748B', fontFamily: 'Cairo, sans-serif',
                                animation: `${pulse} 1.5s ease-in-out infinite`,
                            }}
                        >
                            {loadingResources
                                ? (t('GEO_EXTRACTOR.loadingResources') || 'Loading resources...')
                                : (t('GEO_EXTRACTOR.loadingZones') || 'Processing geofences...')}
                        </Typography>
                        {loadingZones && (
                            <LinearProgress
                                sx={{
                                    width: 200, borderRadius: 4,
                                    '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #4ECDC4, #6C9BCF)' },
                                    background: '#E2E8F0',
                                }}
                            />
                        )}
                    </Box>
                )
            }

            {
                error && !loadingResources && !loadingZones && (
                    <Box
                        sx={{
                            mt: 4, textAlign: 'center', py: 8,
                            animation: `${fadeInUp} 0.5s ease-out`,
                        }}
                    >
                        <Typography sx={{ fontSize: 48, mb: 1 }}>⚠️</Typography>
                        <Typography sx={{ fontSize: 16, color: '#EF4444', fontFamily: 'Cairo, sans-serif', fontWeight: 600 }}>
                            {error}
                        </Typography>
                    </Box>
                )
            }

            {
                !loadingResources && !loadingZones && !error && rows.length === 0 && resources.length > 0 && (
                    <Box
                        sx={{
                            mt: 4, textAlign: 'center', py: 8,
                            animation: `${fadeInUp} 0.5s ease-out`,
                        }}
                    >
                        <FolderOpenIcon sx={{ fontSize: 64, color: '#CBD5E1', mb: 2 }} />
                        <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#64748B', fontFamily: 'Cairo, sans-serif' }}>
                            {t('GEO_EXTRACTOR.noData') || 'No WenkTag geofences found'}
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: '#94A3B8', fontFamily: 'Cairo, sans-serif', mt: 0.5 }}>
                            {t('GEO_EXTRACTOR.noDataDesc') || 'This resource has no WenkTag-formatted geofences. Try selecting another resource.'}
                        </Typography>
                    </Box>
                )
            }

            {/* ── Data Table ──────────────────────────────────────────── */}
            {
                !loadingResources && !loadingZones && rows.length > 0 && viewMode === 'table' && (
                    <Paper
                        elevation={0}
                        sx={{
                            mt: 2, borderRadius: 3, overflow: 'hidden',
                            border: '1px solid #E2E8F0',
                            animation: `${fadeInUp} 0.5s ease-out 0.15s both`,
                        }}
                    >
                        <TableContainer sx={{ maxHeight: 'calc(100vh - 360px)' }}>
                            <Table stickyHeader size="small" sx={{ '& .MuiTableCell-root': { borderLeft: 'none !important', borderRight: 'none !important' } }}>
                                <TableHead sx={{ height: "50px" }}>
                                    <TableRow sx={{
                                        background: gridtheme?.primaryColor || '#707070',
                                        '& .MuiTableCell-stickyHeader': {
                                            background: gridtheme?.primaryColor || '#707070',
                                        },
                                        '& .MuiTableCell-root': {
                                            background: gridtheme?.primaryColor || '#707070',
                                        }
                                    }}>
                                        {/* Row number column */}
                                        <TableCell
                                            sx={{
                                                fontFamily: 'Cairo, sans-serif', fontWeight: 600,
                                                fontSize: 13, color: '#FFFFFF',
                                                borderRight: 'none', borderLeft: 'none', borderBottom: 'none',
                                                whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 2,
                                                width: 50, textAlign: 'center',
                                            }}
                                        >
                                            #
                                        </TableCell>
                                        {columns.map((col) => (
                                            <TableCell
                                                key={col.id}
                                                sortDirection={orderBy === col.id ? order : false}
                                                sx={{
                                                    fontFamily: 'Cairo, sans-serif', fontWeight: 600,
                                                    fontSize: 13, color: '#FFFFFF',
                                                    borderRight: 'none', borderLeft: 'none', borderBottom: 'none',
                                                    whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 2,
                                                }}
                                            >
                                                <TableSortLabel
                                                    active={orderBy === col.id}
                                                    direction={orderBy === col.id ? order : 'asc'}
                                                    onClick={() => handleSort(col.id)}
                                                    sx={{
                                                        color: '#FFFFFF !important',
                                                        '&:hover': { color: '#FFFFFF !important' },
                                                        '&.Mui-active': { color: '#FFFFFF !important' },
                                                        '& .MuiTableSortLabel-icon': { color: '#FFFFFF !important' },
                                                    }}
                                                >
                                                    {col.label}
                                                </TableSortLabel>
                                            </TableCell>
                                        ))}
                                        {/* Actions header */}
                                        <TableCell
                                            sx={{
                                                fontFamily: 'Cairo, sans-serif', fontWeight: 600,
                                                fontSize: 13, color: '#FFFFFF',
                                                borderRight: 'none', borderLeft: 'none', borderBottom: 'none',
                                                whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 2,
                                                textAlign: 'center', width: 60,
                                            }}
                                        >
                                            {isRTL ? 'الإجراءات' : 'Actions'}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedRows.map((row, idx) => (
                                        <TableRow
                                            key={idx}
                                            hover
                                            sx={{
                                                transition: 'background 0.2s ease',
                                                '&:hover': { background: 'rgba(78, 205, 196, 0.04) !important' },
                                                '&:nth-of-type(even)': { background: '#FAFBFC' },
                                            }}
                                        >
                                            <TableCell
                                                sx={{
                                                    fontFamily: 'Cairo, sans-serif', fontSize: 12,
                                                    color: '#94A3B8', textAlign: 'center',
                                                    borderBottom: '1px solid #F1F5F9',
                                                }}
                                            >
                                                {(rowsPerPage === -1 ? 0 : page * rowsPerPage) + idx + 1}
                                            </TableCell>
                                            {columns.map((col) => (
                                                <TableCell
                                                    key={col.id}
                                                    sx={{
                                                        fontFamily: 'Cairo, sans-serif', fontSize: 13,
                                                        color: row[col.id] && row[col.id] !== '----' ? '#1E293B' : '#CBD5E1',
                                                        borderBottom: '1px solid #F1F5F9',
                                                        whiteSpace: 'nowrap',
                                                        maxWidth: 200,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    }}
                                                >
                                                    {row[col.id] || '----'}
                                                </TableCell>
                                            ))}
                                            {/* Actions column */}
                                            <TableCell
                                                sx={{
                                                    borderBottom: '1px solid #F1F5F9',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                {(row.lat && row.lng) || row.imageUrl ? (
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            setActionsMenuAnchorEl(e.currentTarget);
                                                            setActionsMenuRow(row);
                                                        }}
                                                        sx={{
                                                            color: '#64748B',
                                                            '&:hover': { background: 'rgba(100, 116, 139, 0.1)' },
                                                        }}
                                                    >
                                                        <MoreVertIcon fontSize="small" />
                                                    </IconButton>
                                                ) : (
                                                    <Typography sx={{ color: '#CBD5E1', fontSize: 12 }}>—</Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            component="div"
                            count={filteredRows.length}
                            page={page}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                            rowsPerPageOptions={[10, 25, 50, 100, { value: -1, label: isRTL ? 'الكل' : 'All' }]}
                            labelRowsPerPage={isRTL ? 'صفوف لكل صفحة:' : 'Rows per page:'}
                            labelDisplayedRows={({ from, to, count }) =>
                                isRTL ? `${from}–${to} من ${count}` : `${from}–${to} of ${count}`
                            }
                            sx={{
                                borderTop: '1px solid #E2E8F0',
                                '& .MuiTablePagination-toolbar': {
                                    direction: 'ltr',
                                    justifyContent: 'flex-end',
                                    gap: 1,
                                },
                                '& .MuiTablePagination-spacer': {
                                    display: 'none',
                                },
                                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                    fontFamily: 'Cairo, sans-serif', fontSize: 13, color: '#64748B',
                                },
                                '& .MuiTablePagination-select': {
                                    fontFamily: 'Cairo, sans-serif',
                                },
                                '& p': {
                                    marginTop: 0,
                                    marginBottom: 0
                                },
                            }}
                        />
                    </Paper>
                )
            }

            {/* ── Actions Dropdown Menu ─────────────────────────────────── */}
            <Menu
                anchorEl={actionsMenuAnchorEl}
                open={Boolean(actionsMenuAnchorEl)}
                onClose={() => { setActionsMenuAnchorEl(null); setActionsMenuRow(null); }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                        minWidth: 180,
                    },
                }}
            >
                {actionsMenuRow?.lat && actionsMenuRow?.lng && (
                    <MenuItem
                        onClick={() => {
                            handleOpenLocation(actionsMenuRow.lat, actionsMenuRow.lng);
                            setActionsMenuAnchorEl(null);
                            setActionsMenuRow(null);
                        }}
                        sx={{ fontFamily: 'Cairo, sans-serif', gap: 1 }}
                    >
                        <ListItemIcon sx={{ minWidth: '32px !important' }}>
                            <LocationOnIcon fontSize="small" sx={{ color: '#4ECDC4' }} />
                        </ListItemIcon>
                        <ListItemText
                            primary={isRTL ? 'الموقع' : 'Location'}
                            primaryTypographyProps={{ fontFamily: 'Cairo, sans-serif', fontSize: 14 }}
                        />
                    </MenuItem>
                )}
                {actionsMenuRow?.imageUrl && (
                    <MenuItem
                        onClick={() => {
                            handleOpenImage(actionsMenuRow.imageUrl, actionsMenuRow.name);
                            setActionsMenuAnchorEl(null);
                            setActionsMenuRow(null);
                        }}
                        sx={{ fontFamily: 'Cairo, sans-serif', gap: 1 }}
                    >
                        <ListItemIcon sx={{ minWidth: '32px !important' }}>
                            <ImageIcon fontSize="small" sx={{ color: '#6C9BCF' }} />
                        </ListItemIcon>
                        <ListItemText
                            primary={isRTL ? 'عرض الصورة' : 'View Image'}
                            primaryTypographyProps={{ fontFamily: 'Cairo, sans-serif', fontSize: 14 }}
                        />
                    </MenuItem>
                )}
            </Menu>

            {/* ── Map View ────────────────────────────────────────────── */}
            {
                !loadingResources && !loadingZones && rows.length > 0 && viewMode === 'map' && (
                    <Paper
                        elevation={0}
                        sx={{
                            mt: isMapFullscreen ? 0 : 2,
                            borderRadius: isMapFullscreen ? 0 : 3,
                            overflow: 'hidden',
                            border: isMapFullscreen ? 'none' : '1px solid #E2E8F0',
                            animation: `${fadeInUp} 0.5s ease-out 0.15s both`,
                            height: isMapFullscreen ? '100vh' : 'calc(100vh - 280px)',
                            position: isMapFullscreen ? 'fixed' : 'relative',
                            top: isMapFullscreen ? 0 : 'auto',
                            left: isMapFullscreen ? 0 : 'auto',
                            right: isMapFullscreen ? 0 : 'auto',
                            bottom: isMapFullscreen ? 0 : 'auto',
                            zIndex: isMapFullscreen ? 9999 : 1,
                        }}
                    >
                        {/* Fullscreen Toggle Button */}
                        <IconButton
                            onClick={() => setIsMapFullscreen(!isMapFullscreen)}
                            sx={{
                                position: 'absolute',
                                top: 12,
                                right: isRTL ? 'auto' : 12,
                                left: isRTL ? 12 : 'auto',
                                zIndex: 1000,
                                background: '#fff',
                                boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
                                borderRadius: 2,
                                width: 38,
                                height: 38,
                                '&:hover': { background: '#f8fafc' },
                            }}
                        >
                            {isMapFullscreen ? (
                                <FullscreenExitIcon sx={{ color: '#1E293B' }} />
                            ) : (
                                <FullscreenIcon sx={{ color: '#1E293B' }} />
                            )}
                        </IconButton>

                        <MapContainer
                            center={[33.3, 44.4]}
                            zoom={12}
                            style={{ height: '100%', width: '100%' }}
                            bounds={filteredRows
                                .filter(r => r.lat && r.lng)
                                .map(r => [r.lat, r.lng])}
                            boundsOptions={{ padding: [50, 50] }}
                        >
                            <LayersControl position="bottomright">
                                <BaseLayer checked name={isRTL ? "الخريطة القياسية" : "Standard Map"}>
                                    <TileLayer
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    />
                                </BaseLayer>
                                <BaseLayer name={isRTL ? "صورة القمر الصناعي" : "Satellite View"}>
                                    <TileLayer
                                        attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EBP, and the GIS User Community'
                                        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                                    />
                                </BaseLayer>
                            </LayersControl>
                            {filteredRows
                                .filter(r => r.lat && r.lng)
                                .map((row, idx) => (
                                    <Marker
                                        key={idx}
                                        position={[row.lat, row.lng]}
                                        icon={customIcon}
                                    >
                                        <Popup
                                            maxWidth={450}
                                            minWidth={380}
                                            className="square-popup"
                                        >
                                            <Box
                                                // dir={isRTL ? 'rtl' : 'ltr'}
                                                sx={{
                                                    fontFamily: 'Cairo, sans-serif',
                                                    p: 0,
                                                    overflow: 'visible',
                                                    textAlign: isRTL ? 'right' : 'left',
                                                    width: 380,
                                                    display: 'flex',
                                                    flexDirection: 'column'
                                                }}
                                            >
                                                {/* Header: Name + Image */}
                                                <Box sx={{
                                                    display: 'flex',
                                                    gap: 1.5,
                                                    alignItems: 'center',
                                                    p: 1.5,
                                                    borderBottom: '1px solid #F1F5F9',
                                                    backgroundColor: '#fff',
                                                    // flexDirection: 'row-reverse !important',
                                                    // justifyContent: 'space-between'
                                                }}>
                                                    {row.imageUrl && (
                                                        <Box
                                                            onClick={() => setImagePreview({ open: true, url: row.imageUrl.replace('b=64', 'b=1024'), name: row.name })}
                                                            sx={{
                                                                width: 50, height: 50,
                                                                borderRadius: 2,
                                                                overflow: 'hidden',
                                                                flexShrink: 0,
                                                                border: '1.5px solid #4ECDC4',
                                                                cursor: 'pointer',
                                                            }}
                                                        >
                                                            <img
                                                                src={row.imageUrl.replace('b=64', 'b=128')}
                                                                alt={row.name}
                                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                            />
                                                        </Box>
                                                    )}
                                                    <Typography
                                                        sx={{
                                                            fontFamily: 'Cairo, sans-serif',
                                                            fontWeight: 800,
                                                            fontSize: 15,
                                                            color: '#1E293B',
                                                            lineHeight: 1.2,
                                                            // flex: 1,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            // display: '-webkit-box',
                                                            // WebkitLineClamp: 2,
                                                            // WebkitBoxOrient: 'vertical',
                                                        }}
                                                    >
                                                        {row.name}
                                                    </Typography>
                                                </Box>

                                                {/* 4 Fields Grid - Core Content */}
                                                <Box sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                                    gap: '12px 20px',
                                                    p: 2,
                                                    // flex: 1,
                                                }}>
                                                    {/* Area */}
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: 0.5,
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            backgroundColor: '#e0ecebff',
                                                            borderRadius: '10px',
                                                        }}
                                                    >
                                                        <Typography sx={{ fontSize: 9, color: '#94A3B8', fontWeight: 700, mb: -0.2, textTransform: 'uppercase' }}>
                                                            {isRTL ? 'المنطقة' : 'Area'}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 12, color: '#1E293B', fontWeight: 600 }}>
                                                            {row.area || '----'}
                                                        </Typography>
                                                    </Box>

                                                    {/* Class */}
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: 0.5,
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            backgroundColor: '#e0ecebff',
                                                            borderRadius: '10px',
                                                        }}
                                                    >
                                                        <Typography sx={{ fontSize: 9, color: '#94A3B8', fontWeight: 700, mb: -0.2, textTransform: 'uppercase' }}>
                                                            {isRTL ? 'الفئة' : 'Class'}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 12, color: '#1E293B', fontWeight: 600 }}>
                                                            {row.classif || '----'}
                                                        </Typography>
                                                    </Box>

                                                    {/* Agent/Driver */}
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: 0.5,
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            backgroundColor: '#e0ecebff',
                                                            borderRadius: '10px',
                                                        }}
                                                    >
                                                        <Typography sx={{ fontSize: 9, color: '#94A3B8', fontWeight: 700, mb: -0.2, textTransform: 'uppercase' }}>
                                                            {isRTL ? 'المسؤول' : 'Agent'}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 12, color: '#1E293B', fontWeight: 600 }}>
                                                            {row.driver || '----'}
                                                        </Typography>
                                                    </Box>

                                                    {/* Phone */}
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: 0.5,
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            backgroundColor: '#e0ecebff',
                                                            borderRadius: '10px',
                                                        }}
                                                    >
                                                        <Typography sx={{ fontSize: 9, color: '#94A3B8', fontWeight: 700, mb: -0.2, textTransform: 'uppercase' }}>
                                                            {isRTL ? 'الهاتف' : 'Phone'}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 12, color: '#4ECDC4', fontWeight: 700 }}>
                                                            {row.phone || '----'}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                {/* Footer: Date + Details Action */}
                                                <Box sx={{
                                                    p: 1.5,
                                                    background: '#F8FAFC',
                                                    borderTop: '1px solid #F1F5F9',
                                                }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                                        <Typography sx={{ fontSize: 9.5, color: '#64748B', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <RefreshIcon sx={{ fontSize: 12, color: '#4ECDC4' }} />
                                                            {row.captureTime}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 9, color: '#CBD5E1', fontWeight: 600 }}>
                                                            {row.lat.toFixed(6)}, {row.lng.toFixed(6)}
                                                        </Typography>
                                                    </Box>

                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Box
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedRowForDetails(row);
                                                                setDetailsDialogOpen(true);
                                                            }}
                                                            sx={{
                                                                flex: 1,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: 0.8,
                                                                textDecoration: 'none',
                                                                color: '#4ECDC4',
                                                                fontSize: 11.5,
                                                                fontWeight: 800,
                                                                py: 0.8,
                                                                borderRadius: 1.5,
                                                                background: '#fff',
                                                                border: '1.5px solid #4ECDC4',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.2s',
                                                                '&:hover': { background: 'rgba(78, 205, 196, 0.05)', transform: 'translateY(-1px)' }
                                                            }}
                                                        >
                                                            <FullscreenIcon sx={{ fontSize: 16 }} />
                                                            {isRTL ? 'كامل التفاصيل' : 'Full Details'}
                                                        </Box>
                                                        <IconButton
                                                            component="a"
                                                            href={`https://www.google.com/maps?q=${row.lat},${row.lng}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            size="small"
                                                            sx={{
                                                                borderRadius: 1.5,
                                                                background: '#4ECDC4',
                                                                color: '#fff',
                                                                '&:hover': { background: '#44A08D' }
                                                            }}
                                                        >
                                                            <OpenInNewIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Popup>
                                    </Marker>
                                ))}
                        </MapContainer>
                    </Paper>
                )
            }

            {/* ── Image Preview Dialog ─────────────────────────────── */}
            <Dialog
                open={imagePreview.open}
                onClose={handleCloseImage}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        overflow: 'hidden',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
                    }
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2.5,
                        py: 1.5,
                        background: '#fff',
                        borderBottom: '2px solid #E2E8F0',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                            width: 32, height: 32, borderRadius: 2,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: 'rgba(78, 205, 196, 0.1)',
                        }}>
                            <ImageIcon sx={{ fontSize: 18, color: '#4ECDC4' }} />
                        </Box>
                        <Typography
                            sx={{
                                fontFamily: 'Cairo, sans-serif',
                                fontWeight: 600,
                                fontSize: 15,
                                color: '#1E293B',
                            }}
                        >
                            {imagePreview.name || (isRTL ? 'صورة' : 'Image')}
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={handleCloseImage}
                        size="small"
                        sx={{
                            color: '#94A3B8',
                            '&:hover': { color: '#1E293B', background: '#F1F5F9' },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        background: '#F8FAFC',
                        p: 3,
                        minHeight: 300,
                    }}
                >
                    {imagePreview.url && (
                        <Box
                            component="img"
                            src={imagePreview.url.replace('b=64', 'b=0')}
                            alt={imagePreview.name}
                            sx={{
                                maxWidth: '100%',
                                maxHeight: '65vh',
                                objectFit: 'contain',
                                borderRadius: 2,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                            }}
                        />
                    )}
                </Box>
            </Dialog>

            {/* Full Details Dialog */}
            <Dialog
                open={detailsDialogOpen}
                onClose={() => setDetailsDialogOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        maxHeight: '85vh',
                    }
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 3,
                        py: 2,
                        background: '#fff',
                        borderBottom: '2px solid #F1F5F9',
                        dir: isRTL ? 'rtl' : 'ltr'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 36, height: 36, borderRadius: 2,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: 'rgba(78, 205, 196, 0.1)',
                        }}>
                            <FolderOpenIcon sx={{ fontSize: 20, color: '#4ECDC4' }} />
                        </Box>
                        <Typography
                            sx={{
                                fontFamily: 'Cairo, sans-serif',
                                fontWeight: 800,
                                fontSize: 18,
                                color: '#1E293B',
                            }}
                        >
                            {selectedRowForDetails?.name || (isRTL ? 'التفاصيل' : 'Details')}
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={() => setDetailsDialogOpen(false)}
                        size="small"
                        sx={{
                            color: '#94A3B8',
                            '&:hover': { color: '#1E293B', background: '#F1F5F9' },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 22 }} />
                    </IconButton>
                </Box>
                <Box
                    dir={isRTL ? 'rtl' : 'ltr'}
                    sx={{
                        p: 4,
                        background: '#F8FAFC',
                        overflowY: 'auto',
                    }}
                >
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                        gap: 3
                    }}>
                        {selectedRowForDetails && columns
                            .filter(col => col.id !== 'name')
                            .filter(col => selectedRowForDetails[col.id] && selectedRowForDetails[col.id] !== '----' && selectedRowForDetails[col.id] !== 'null')
                            .map((col) => (
                                <Box key={col.id} sx={{
                                    p: 2,
                                    background: '#fff',
                                    borderRadius: 3,
                                    border: '1px solid #E2E8F0',
                                    transition: 'all 0.2s',
                                    '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.05)', transform: 'translateY(-2px)' }
                                }}>
                                    <Typography sx={{
                                        fontSize: 11,
                                        color: '#94A3B8',
                                        fontWeight: 700,
                                        mb: 0.5,
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.5
                                    }}>
                                        {col.label}
                                    </Typography>
                                    <Typography sx={{
                                        fontSize: 15,
                                        color: (col.id === 'phone' || col.id === 'captureTime') ? '#4ECDC4' : '#1E293B',
                                        fontWeight: 700
                                    }}>
                                        {selectedRowForDetails[col.id]}
                                    </Typography>
                                </Box>
                            ))}
                    </Box>
                </Box>
            </Dialog>
        </Box >
    );
}
